import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ValidateBookingRequest {
  productKey: string;
  isAggreementRead: boolean;
  note: string;
  passengers: Passenger[];
  traceId?: string;
  transactionKey?: string; // Backend otomatik null set edecek
}

export interface Passenger {
  key: string;
  isMaster: boolean;
  name: string;
  surname: string;
  birthday: string | null;
  gender: number; // 0=Male, 1=Female, 2=Unknown
  tcKimlikNo: string;
  hesCode: string;
  email: string;
  age: number; // Yetişkin için 35 varsayılan, çocuk için search'ten gelen yaş
  address: {
    street: string;
    postalCode: string;
    country: {
      id: number;
      label: string;
      parentId?: number; // 13
      parentRegion?: string; // null
    };
  };
  phoneNumbers: PhoneNumber[];
  country: {
    id: number;
    label: string;
  };
  passengerType: number; // 0=Adult, 1=Child, 2=Infant
  id?: number; // 0
  passportTypeId?: number; // 0
  mernisQueryStatus?: string; // "WrongInformation"
  milesCode?: string; // null
  passportNumber?: string; // null
  prefix?: string; // null
  zipCode?: string; // null
}

export interface PhoneNumber {
  countryCode: string;
  areaCode: string;
  number: string;
  type: 'Mobile' | 'Home' | 'Work';
}

export interface ValidateBookingResponse {
  success: boolean;
  isBookable: boolean;
  reasons: BookingReason[];
  traceId?: string;
  productKey?: string; // Response'da var
  data?: any; // Tüm WCF response'u
}

export interface BookingReason {
  code: string;
  description: string;
}


// Eski BookRequest interface'i kaldırıldı - Yeni API için BookRequest satır 230'da tanımlı

export interface OldBookResponse {
  success: boolean;
  message?: string;
  pnrCode?: string;
  reservationCode?: string;
  bookingDetails?: any;
}

export interface StartTransactionRequest {
  productKeys: string[]; // Backend'e gönderilen yapıya uygun (küçük harf)
}

export interface StartTransactionResponse {
  success: boolean; // Backend'den gelen yanıt yapısına uygun (küçük harf)
  traceId?: string;
  data?: {
    productKeys: string[];
    transactionKey: string; // Küçük harf
    bookCompletionAsynchronous: boolean;
    discountCouponTransactionId?: string;
    messages: string[];
  };
  message?: string;
  detail?: string;
}

// GetSession için interface'ler
export interface GetSessionRequest {
  traceId: string;
}

export interface GetSessionResponse {
  success: boolean;
  traceId: string;
  clientSessionKey: string;
  data?: {
    sessionId: string;
    expiresAt: string;
  };
  message?: string;
}

// GetAvailableCampaigns için interface'ler
export interface GetAvailableCampaignsRequest {
  traceId: string;
  productKey: string;
}

export interface Campaign {
  campaignKey: string;
  name: string;
  description: string;
  discountRate: number;
  validUntil: string;
}

export interface GetAvailableCampaignsResponse {
  success: boolean;
  traceId: string;
  campaigns: Campaign[];
  campaignCount: number;
  message?: string;
}

// GetInstallment için interface'ler
export interface GetInstallmentRequest {
  traceId: string;
  productKey: string;
  bankCode: string;
  cardBin: string;
}

export interface Installment {
  installmentKey: string;
  installmentCount: number;
  totalAmount: number;
  monthlyPayment: number;
}

export interface GetInstallmentResponse {
  success: boolean;
  installments: Installment[];
  message?: string;
}

// İyzico Payment Initialize için interface'ler
export interface BasketItem {
  id: string;  // ✅ camelCase
  name: string;
  category1: string;
  itemType: string;
  price: number;
}

export interface Buyer {
  id: string;  // ✅ camelCase
  name: string;
  surname: string;
  identityNumber: string;
  email: string;
  gsmNumber: string;
  registrationDate: string;
  lastLoginDate: string;
  registrationAddress: string;
  ip: string;
  city: string;
  country: string;
  zipCode: string;
}

export interface BillingAddress {
  contactName: string;  // ✅ camelCase
  city: string;
  country: string;
  address: string;
  zipCode: string;
}

export interface PaymentCard {
  cardHolderName: string;
  cardNumber: string;
  expireMonth: string;
  expireYear: string;
  cvc: string;
  cardType: string;  // "1" = Visa, "2" = MasterCard (NestPay için zorunlu)
}

// NestPay Initialize Payment Request (3D_PAY)
export interface InitializePaymentRequest {
  traceId: string;
  transactionKey: string;
  productKey: string;
  passengerInfo: string;    // JSON string - yolcu bilgileri
  contactInfo: string;      // JSON string - iletişim bilgileri
  price: number;            // Ödeme tutarı (komisyonsuz)
  paidPrice: number;        // Ödeme tutarı (komisyon dahil)
  currency: string;         // "TRY"
  installment: number;      // Taksit sayısı (0 veya 1 = tek çekim)
  paymentCard: PaymentCard;
  
  // Opsiyonel otel bilgileri
  hotelName?: string;
  roomName?: string;
  checkInDate?: string;
  checkOutDate?: string;

  // Fatura bilgileri
  invoiceType?: 'PERSONAL' | 'CORPORATE';
  tcNumber?: string;
  personalName?: string;
  personalSurname?: string;
  taxOffice?: string;
  taxNumber?: string;
  companyName?: string;
  companyAddress?: string;
  companyCity?: string;
  companyPostalCode?: string;
  companyPhoneNumber?: string;
  companyEmail?: string;
}

// NestPay Initialize Payment Response (3D_PAY)
export interface InitializePaymentResponse {
  success: boolean;
  message: string;
  data?: {
    orderId: string;                      // "PUS-xxxx-20260309..."
    gateUrl: string;                      // "https://sanalpos.isbank.com.tr/fim/est3dgate"
    formParams: Record<string, string>;   // Tüm form parametreleri (hash dahil)
  };
}

// Book için interface'ler
export interface BookRequest {
  traceId: string;
  productKey: string;
  passengers: Passenger[];
  contactInfo: {
    email: string;
    phone: string;
  };
}

export interface BookResponse {
  success: boolean;
  traceId: string;
  bookingCode: string;
  message?: string;
  data?: {
    bookingCode: string;
    status: string;
    hotelName: string;
    roomType: string;
    checkIn: string;
    checkOut: string;
    totalPrice: number;
    currency: string;
    passengers: number;
  };
}

// EndTransaction için interface'ler
export interface EndTransactionRequest {
  transactionKey: string;
  traceId: string;
}

export interface EndTransactionResponse {
  success: boolean;
  message: string;
  transactionKey: string;
}

// Booking Result için interface (ConversationId ile sorgulama)
export interface BookingResultResponse {
  Success: boolean;
  BookingNumber: string;
  Price: number;
  PaidPrice: number;
  Currency: string;
  Status: string;
  BookingCompletedAt: string;
  message?: string;
}

// GetTransactionSummary için interface'ler
export interface GetTransactionSummaryRequest {
  transactionKey: string;
  traceId: string;
}

export interface Price {
  currency: number | null;
  value: number;
}

export interface Payment {
  // Payment detayları - WCF'den gelen yapıya göre genişletilebilir
  [key: string]: any;
}

export interface Campaign {
  // Campaign detayları - WCF'den gelen yapıya göre genişletilebilir
  [key: string]: any;
}

export interface Message {
  // Message detayları - WCF'den gelen yapıya göre genişletilebilir
  [key: string]: any;
}

export interface GetTransactionSummaryResponse {
  success: boolean;
  data?: {
    balance: Price;
    basketLeastwiseAmount: Price;
    campaigns: Campaign[];
    currentCampaingAmount: Price;
    isBookable: boolean;
    isCompleted: boolean;
    isPayablePartail: boolean;
    mainCurrencyBalance: Price;
    mainCurrencyBasketLeastwise: Price;
    payment3DIsRequired: boolean;
    paymentCashLimit: number;
    payments: Payment[] | null;
    scopePaymentTypes: number[];
    totalDiscountRate: number;
    totalPayment: Price;
    diagnostics: any;
    messages: Message[] | null;
    traceId: string | null;
  };
  message?: string;
  detail?: string;
  code?: string;
}



@Injectable({
  providedIn: 'root'
})
export class BookingService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  validateBooking(request: ValidateBookingRequest, searchParams?: any): Observable<ValidateBookingResponse> {
    
    return this.http.post<ValidateBookingResponse>(`${this.apiUrl}/ValidateBooking`, request, {
      withCredentials: true // Cookie'leri dahil et
    }).pipe(
      catchError(error => {
        // Eğer search session bulunamadı hatası ise ve searchParams varsa, yeniden search yap
        if (error?.error?.message?.includes('Geçerli bir arama oturumu bulunamadı') && searchParams) {
          return this.refreshSearchAndValidate(request, searchParams);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Search session'ını yeniler ve tekrar validate booking yapar
   * @param request ValidateBooking request
   * @param searchParams Search parametreleri
   * @returns Observable<ValidateBookingResponse>
   */
  private refreshSearchAndValidate(request: ValidateBookingRequest, searchParams: any): Observable<ValidateBookingResponse> {
    return this.http.post(`${this.apiUrl}/Search`, searchParams, {
      withCredentials: true
    }).pipe(
      switchMap(() => {
        // Search başarılı olduktan sonra tekrar validate booking yap
        return this.http.post<ValidateBookingResponse>(`${this.apiUrl}/ValidateBooking`, request, {
          withCredentials: true
        });
      })
    );
  }

  /**
   * ESKİ Rezervasyon işlemi - Deprecated, yeni API için bookReservation() kullanın
   * @deprecated Kullanmayın - bookReservation() metodunu kullanın
   */
  oldBook(request: any, searchParams?: any): Observable<OldBookResponse> {
    return this.http.post<OldBookResponse>(`${this.apiUrl}/Book`, request, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        // Eğer search session bulunamadı hatası ise ve searchParams varsa, yeniden search yap
        if (error?.error?.message?.includes('Geçerli bir arama oturumu bulunamadı') && searchParams) {
          return this.refreshSearchAndBook(request, searchParams);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Search session'ını yeniler ve tekrar book yapar
   * @param request Book request
   * @param searchParams Search parametreleri
   * @returns Observable<OldBookResponse>
   */
  private refreshSearchAndBook(request: any, searchParams: any): Observable<OldBookResponse> {
    return this.http.post(`${this.apiUrl}/Search`, searchParams, {
      withCredentials: true
    }).pipe(
      switchMap(() => {
        // Search başarılı olduktan sonra tekrar book yap
        return this.http.post<OldBookResponse>(`${this.apiUrl}/Book`, request, {
          withCredentials: true
        });
      })
    );
  }

  /**
   * Transaction başlatır
   * @param productKeys Ürün anahtarları
   * @param searchParams Search parametreleri (session yenileme için)
   * @returns Observable<StartTransactionResponse>
   */
  startTransaction(productKeys: string[], searchParams?: any): Observable<StartTransactionResponse> {
    // TraceId kontrolü ekle
    const traceId = this.getTraceIdFromCookie();
    if (!traceId) {
      return throwError(() => new Error('Geçerli bir arama oturumu bulunamadı. Lütfen önce arama yapın.'));
    }

    const request: StartTransactionRequest = {
      productKeys: productKeys // Backend'e gönderilen yapıya uygun (küçük harf)
    };


    return this.http.post<StartTransactionResponse>(`${this.apiUrl}/Transaction/StartTransaction`, request, {
      withCredentials: true // Cookie'leri dahil et
    }).pipe(
      catchError(error => {
        
        // Eğer session bulunamadı hatası ise ve searchParams varsa, yeniden search yap
        if ((error?.error?.message?.includes('Geçerli bir arama oturumu bulunamadı') || 
             error?.error?.message?.includes('session') ||
             error?.error?.message?.includes('oturum')) && searchParams) {
          return this.refreshSearchAndStartTransaction(productKeys, searchParams);
        }
        
        return throwError(error);
      })
    );
  }

  /**
   * Search session'ını yeniler ve tekrar start transaction yapar
   * @param productKeys Ürün anahtarları
   * @param searchParams Search parametreleri
   * @returns Observable<StartTransactionResponse>
   */
  private refreshSearchAndStartTransaction(productKeys: string[], searchParams: any): Observable<StartTransactionResponse> {
    return this.http.post(`${this.apiUrl}/Search`, searchParams, {
      withCredentials: true
    }).pipe(
      switchMap(() => {
        // Search başarılı olduktan sonra tekrar start transaction yap
        return this.http.post<StartTransactionResponse>(`${this.apiUrl}/Transaction/StartTransaction`, {
          productKeys: productKeys // Backend'e gönderilen yapıya uygun (küçük harf)
        }, {
          withCredentials: true
        });
      })
    );
  }

  /**
   * Cookie'den traceId değerini alır
   * @param cookieName Cookie adı (varsayılan: 'TraceId')
   * @returns string | null
   */
  getTraceIdFromCookie(cookieName: string = 'TraceId'): string | null {
    if (typeof document === 'undefined') {
      return null; // Server-side rendering kontrolü
    }

    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      
      if (name === cookieName) {
        return decodeURIComponent(value);
      }
    }
    
    return null;
  }


  /**
   * API validation hatalarını kullanıcı dostu mesajlara çevirir
   * @param error HTTP error response
   * @returns string - Kullanıcı dostu hata mesajı
   */
  parseValidationError(error: any): string {
    if (error?.error?.errors) {
      const errors = error.error.errors;
      const errorMessages: string[] = [];
      
      Object.keys(errors).forEach(key => {
        if (Array.isArray(errors[key])) {
          errors[key].forEach((message: string) => {
            // Field isimlerini Türkçe'ye çevir
            const friendlyFieldName = this.getFieldDisplayName(key);
            const friendlyMessage = message.replace(/The .+ field/, friendlyFieldName);
            errorMessages.push(friendlyMessage);
          });
        }
      });
      
      return errorMessages.length > 0 
        ? errorMessages.join(', ') 
        : 'Rezervasyon bilgilerinde eksiklik var.';
    }
    
    if (error?.error?.title) {
      return error.error.title;
    }
    
    return 'Rezervasyon doğrulanırken bir hata oluştu.';
  }


  /**
   * API field isimlerini kullanıcı dostu Türkçe isimlere çevirir
   * @param fieldName API field ismi
   * @returns string - Türkçe field ismi
   */
  private getFieldDisplayName(fieldName: string): string {
    const fieldMap: { [key: string]: string } = {
      'Passengers[0].HesCode': 'Ana yolcu HES kodu',
      'Passengers[1].HesCode': 'İkinci yolcu HES kodu',
      'Passengers[0].TcKimlikNo': 'Ana yolcu TC kimlik no',
      'Passengers[1].TcKimlikNo': 'İkinci yolcu TC kimlik no',
      'Passengers[0].Name': 'Ana yolcu adı',
      'Passengers[1].Name': 'İkinci yolcu adı',
      'Passengers[0].Surname': 'Ana yolcu soyadı',
      'Passengers[1].Surname': 'İkinci yolcu soyadı',
      'Passengers[0].Email': 'Ana yolcu e-posta',
      'Passengers[1].Email': 'İkinci yolcu e-posta',
      'ProductKey': 'Oda bilgisi'
    };
    
    return fieldMap[fieldName] || fieldName;
  }

  /**
   * Tüm cookie'leri debug için listeler
   */
  debugCookies(): void {
  }

  /**
   * TraceId cookie'sinin varlığını kontrol eder
   * @returns boolean
   */
  hasValidTraceId(): boolean {
    const traceId = this.getTraceIdFromCookie();
    return traceId !== null && traceId.trim() !== '';
  }

  /**
   * İşlem özetini alır
   * @param transactionKey StartTransaction'dan alınan transaction key
   * @returns Observable<GetTransactionSummaryResponse>
   */
  getTransactionSummary(transactionKey: string): Observable<GetTransactionSummaryResponse> {
    const traceId = this.getTraceIdFromCookie() || '';
    
    const request: GetTransactionSummaryRequest = {
      transactionKey: transactionKey,
      traceId: traceId
    };

    
    return this.http.post<GetTransactionSummaryResponse>(`${this.apiUrl}/GetTransactionSummary/get-summary`, request, {
      withCredentials: true // Cookie'leri dahil et
    }).pipe(
      catchError(error => {
        
        // Hata mesajlarını kullanıcı dostu hale getir
        const errorMessage = this.parseGetTransactionSummaryError(error);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * GetTransactionSummary hatalarını kullanıcı dostu mesajlara çevirir
   * @param error HTTP error response
   * @returns string - Kullanıcı dostu hata mesajı
   */
  private parseGetTransactionSummaryError(error: any): string {
    // 400 Bad Request - Eksik parametre
    if (error?.status === 400 && error?.error?.message?.includes('TransactionKey boş olamaz')) {
      return 'İşlem bilgisi eksik. Lütfen ödeme adımını tekrar başlatın.';
    }

    // 400 Bad Request - SOAP hatası
    if (error?.status === 400 && error?.error?.message?.includes('İşlem özeti alınırken bir hata oluştu')) {
      return 'Fiyat bilgisi yüklenirken bir sorun oluştu. Lütfen tekrar deneyin.';
    }

    // 404 Not Found - Yanıt boş
    if (error?.status === 404 && error?.error?.message?.includes('geçerli bir yanıt alınamadı')) {
      return 'Fiyat bilgisi şu an yüklenemiyor. Lütfen biraz bekleyip tekrar deneyin.';
    }

    // 503 Service Unavailable - İletişim hatası
    if (error?.status === 503 && error?.error?.message?.includes('iletişim kurulamadı')) {
      return 'Servislerimize şu an ulaşılamıyor. Lütfen birkaç dakika sonra tekrar deneyin.';
    }

    // Genel hata durumu
    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.message) {
      return error.message;
    }

    return 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.';
  }

  /**
   * Session key alır (ClientSessionKey)
   * @param traceId Arama trace ID'si
   * @returns Observable<GetSessionResponse>
   */
  getSession(traceId: string): Observable<GetSessionResponse> {
    
    const request: GetSessionRequest = {
      traceId: traceId
    };

    return this.http.post<GetSessionResponse>(`${this.apiUrl}/getsession`, request, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        const errorMessage = 'Ödeme oturumu oluşturulamadı. Lütfen tekrar deneyin.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Ürün için kullanılabilir kampanyaları listeler (Opsiyonel)
   * @param traceId Arama trace ID'si
   * @param productKey Ürün anahtarı
   * @returns Observable<GetAvailableCampaignsResponse>
   */
  getAvailableCampaigns(traceId: string, productKey: string): Observable<GetAvailableCampaignsResponse> {
    
    const request: GetAvailableCampaignsRequest = {
      traceId: traceId,
      productKey: productKey
    };

    return this.http.post<GetAvailableCampaignsResponse>(`${this.apiUrl}/getavailablecampaigns`, request, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        const errorMessage = 'Kampanya bilgileri yüklenemedi.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Kart için taksit seçeneklerini getirir (Opsiyonel)
   * @param request Taksit isteği
   * @returns Observable<GetInstallmentResponse>
   */
  getInstallment(request: GetInstallmentRequest): Observable<GetInstallmentResponse> {

    return this.http.post<GetInstallmentResponse>(`${this.apiUrl}/getinstallment`, request, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        const errorMessage = 'Taksit seçenekleri yüklenemedi. Lütfen tekrar deneyin.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * NestPay ile 3D Secure ödeme başlatır (3D_PAY)
   * @param request Ödeme başlatma isteği
   * @returns Observable<InitializePaymentResponse>
   */
  initializePayment(request: InitializePaymentRequest): Observable<InitializePaymentResponse> {

    return this.http.post<InitializePaymentResponse>(`${this.apiUrl}/NestPayPayment/initialize`, request, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        let errorMessage = 'Ödeme işlemi başlatılamadı. Lütfen kart bilgilerinizi kontrol edip tekrar deneyin.';
        
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Session'dan son ödeme bilgisini alır (tek kullanımlık)
   * @returns Observable<LastPaymentResponse>
   */
  getLastPayment(): Observable<any> {

    return this.http.get<any>(`${this.apiUrl}/NestPayPayment/last-payment`, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        let errorMessage = 'Ödeme bilgisi alınamadı. Oturumunuzun süresi dolmuş olabilir.';
        
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * OrderId ile rezervasyon sonucunu alır (NestPay)
   * @param conversationId NestPay OrderId
   * @returns Observable<BookingResultResponse>
   */
  getBookingResult(conversationId: string): Observable<BookingResultResponse> {

    return this.http.get<BookingResultResponse>(`${this.apiUrl}/NestPayPayment/booking-result/${conversationId}`, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        let errorMessage = 'Rezervasyon bilgisi alınamadı. Lütfen müşteri hizmetleri ile iletişime geçin.';
        
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Session'dan son ödeme hatasını alır
   * @returns Observable<LastPaymentErrorResponse>
   */
  getLastPaymentError(): Observable<any> {

    return this.http.get<any>(`${this.apiUrl}/NestPayPayment/last-payment-error`, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        let errorMessage = 'Hata detayı yüklenemedi.';
        
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Rezervasyon oluşturur (Ödeme başarılı olduktan sonra)
   * @param request Rezervasyon isteği
   * @returns Observable<BookResponse>
   */
  bookReservation(request: BookRequest): Observable<BookResponse> {

    return this.http.post<BookResponse>(`${this.apiUrl}/book`, request, {
      withCredentials: true,
      headers: {
        'X-Trace-Id': request.traceId
      }
    }).pipe(
      catchError(error => {
        const errorMessage = 'Rezervasyon oluşturulurken bir sorun oluştu. Lütfen tekrar deneyin.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Transaction'ı kapatır
   * @param transactionKey Transaction anahtarı
   * @returns Observable<EndTransactionResponse>
   */
  endTransaction(transactionKey: string): Observable<EndTransactionResponse> {
    const traceId = this.getTraceIdFromCookie() || '';
    
    const request: EndTransactionRequest = {
      transactionKey: transactionKey,
      traceId: traceId
    };

    return this.http.post<EndTransactionResponse>(`${this.apiUrl}/endtransaction`, request, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        const errorMessage = 'İşlem tamamlanırken bir sorun oluştu.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

}
